import { useState } from "react";
import { XACTIMATE_CATEGORIES, FORMULA_UNIT_VALUES } from "@rebuild/shared";
import { IXactimateToolRowData } from "../../../../../shared/common/Azure";
import { ACTIVITY_OPTIONS } from "./constants";

interface MappingCardEditorProps {
    mapping: IXactimateToolRowData;
    onSave: (updated: IXactimateToolRowData) => Promise<void> | void;
    onCancel: () => void;
    onDelete?: (id: string) => Promise<void> | void;
}

export const MappingCardEditor = ({ mapping, onSave, onCancel, onDelete }: MappingCardEditorProps) => {
    const [cat, setCat] = useState(mapping.cat ?? "");
    const [variable, setVariable] = useState(mapping.variable ?? "");
    const [activity, setActivity] = useState(mapping.activity ?? "");
    const [quantity, setQuantity] = useState(mapping.quantity ?? "");
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!cat || !variable) return;
        const updated: IXactimateToolRowData = {
            ...mapping,
            cat,
            variable,
            activity: activity || undefined,
            quantity: quantity || undefined,
        };
        setSaving(true);
        let succeeded = false;
        try {
            await onSave(updated);
            succeeded = true;
        } catch {
            // Parent surfaces the error toast; stay in edit mode so the user can retry.
        } finally {
            setSaving(false);
        }
        if (succeeded) onCancel();
    };

    const handleDelete = async () => {
        if (!onDelete) return;
        const yes = confirm("Delete this mapping?");
        if (!yes) return;
        setSaving(true);
        try {
            await onDelete(mapping.id);
        } catch {
            // Parent surfaces the error toast; stay mounted so the user can retry.
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-2 space-y-2">
            <div className="flex items-end gap-2 flex-wrap text-xs">
                <div className="flex flex-col gap-0.5">
                    <label className="text-stone-500">CAT</label>
                    <select
                        className="border rounded px-1.5 py-1 w-28"
                        value={cat}
                        onChange={(e) => setCat(e.target.value)}
                    >
                        <option value="">—</option>
                        {XACTIMATE_CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col gap-0.5">
                    <label className="text-stone-500">SEL</label>
                    <input
                        className="border rounded px-1.5 py-1 w-24"
                        value={variable}
                        onChange={(e) => setVariable(e.target.value)}
                    />
                </div>
                <div className="flex flex-col gap-0.5">
                    <label className="text-stone-500">Activity</label>
                    <select
                        className="border rounded px-1.5 py-1 w-32"
                        value={activity}
                        onChange={(e) => setActivity(e.target.value)}
                    >
                        <option value="">—</option>
                        {ACTIVITY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col gap-0.5">
                    <label className="text-stone-500">Unit</label>
                    <select
                        className="border rounded px-1.5 py-1 w-24"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                    >
                        <option value="">—</option>
                        {FORMULA_UNIT_VALUES.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <button
                    className="btn rounded bg-indigo-600 text-white text-xs px-3 py-1 disabled:opacity-50"
                    onClick={handleSave}
                    disabled={saving || !cat || !variable}
                >
                    {saving ? "Saving…" : "Save"}
                </button>
                <button
                    className="btn rounded bg-stone-200 text-stone-700 text-xs px-3 py-1 disabled:opacity-50"
                    onClick={onCancel}
                    disabled={saving}
                >
                    Cancel
                </button>
                {onDelete && (
                    <button
                        className="btn rounded bg-red-500 text-white text-xs px-3 py-1 ml-auto disabled:opacity-50"
                        onClick={handleDelete}
                        disabled={saving}
                    >
                        Delete
                    </button>
                )}
            </div>
        </div>
    );
};
